/**
 * moneroDkgCeremony — relay-based N-party FROST Ed25519 DKG ceremony
 * for Monero. Mirrors `orchardDkgCeremony.ts` but uses the Monero
 * `FromtDkgSession` WASM primitives, which hide the per-round protocol
 * behind a simple `takeMsg` / `feed` loop. Every DKG message is opaque
 * binary; the relay just shuttles them between parties.
 *
 * Ceremony overview (T-of-N, e.g. 2-of-2 in-place upgrade):
 *   1. Initiator (any vault device with the active share) creates a
 *      session and shows a `WB32MDKG:` QR.
 *   2. Each other vault device opens the Cosigner, scans the QR, and
 *      sends `mdkg-join` with its Vultisig `localPartyId`.
 *   3. Initiator collects all joiners, assigns `frostId`s 1..N in
 *      sorted partyId order, builds the canonical `setupBytes` via
 *      `fromtDkgSetupMsgNew`, and broadcasts `mdkg-start` carrying the
 *      setup + frostId map + network/birthday.
 *   4. Every party calls `FromtDkgSession.fromSetup` and drives the
 *      `takeMsg` / `feed` loop. Outgoing messages are routed to the
 *      addressed party via `msgReceiver(msg, idx)`; incoming messages
 *      are unwrapped from the `[sender_frost_id_u16_le][payload]` frame
 *      the WASM expects from `feed`.
 *   5. Once a session's `feed` returns `true`, the party calls
 *      `result()` to extract its `KeyShareBundle` bytes and broadcasts
 *      `mdkg-done` carrying its derived view-key (sanity check).
 *
 * This module re-uses the same encrypted envelope (`base64( AES-GCM(
 * deflate-raw( json ) ) )`) and `HttpRelayTransport` as the Orchard
 * ceremony, so no relay-server changes are required.
 */

import {
	initFromtFrostWasm,
	encodeFromtPartiesData,
	networkByte,
	parseKeyShareBundleHeader,
	type FromtDkgSessionHandle,
	type FromtNetwork,
	type FromtPartyEntry,
} from '../components/toolbox/monero-extensions/fromt/fromtFrostBridge';
import { createTransport, type CosignTransport } from './cosignTransports';
import { generateEntropyBytes } from './secureEntropy';

// ── Constants ────────────────────────────────────────────────────────

const MDKG_QR_PREFIX = 'WB32MDKG';
const MDKG_PROTOCOL_VERSION = 1;
const DEFAULT_RELAY_URL = 'https://cosign.winbit32.com';
const POLL_ACTIVE_MS = 600;
const CEREMONY_TIMEOUT_MS = 10 * 60 * 1000;
/** Cap on how many "drive the WASM until everyone is done" iterations
 *  we allow before declaring the ceremony stuck. The protocol is
 *  3-round under the hood so a healthy run usually completes in
 *  well under 20 inbox/outbox sweeps. */
const MAX_RELAY_DRIVE_LOOPS = 256;

// ── Wire message types ───────────────────────────────────────────────

interface MDkgJoinMsg {
	type: 'mdkg-join';
	partyId: string;
	vaultUid?: string;
}
interface MDkgStartMsg {
	type: 'mdkg-start';
	/** base64 of the canonical `fromtDkgSetupMsgNew` output. */
	setupB64: string;
	/** Ordered partyId list, frostId = index + 1 (matches WASM ordering). */
	partyOrder: string[];
	minSigners: number;
	maxSigners: number;
	network: FromtNetwork;
	/** Birthday height; bigint serialised as decimal string. */
	birthdayStr: string;
	vaultUid?: string;
}
interface MDkgRelayMsg {
	type: 'mdkg-msg';
	fromPartyId: string;
	fromFrostId: number;
	/** base64 of the WASM payload WITHOUT the recipient-prefix bytes. */
	payloadB64: string;
}
interface MDkgDoneMsg {
	type: 'mdkg-done';
	partyId: string;
	/** Hex of the derived 32-byte view key — used as a cross-device
	 *  agreement check so divergence aborts before any storage write. */
	viewKeyHex: string;
}
interface MDkgErrorMsg { type: 'mdkg-error'; message: string; from?: string }

type MDkgMsg =
	| MDkgJoinMsg
	| MDkgStartMsg
	| MDkgRelayMsg
	| MDkgDoneMsg
	| MDkgErrorMsg;

// ── Session + QR helpers ─────────────────────────────────────────────

export interface MoneroDkgSessionParams {
	sessionId: string;
	encKeyHex: string;
	relayUrl: string;
}

export interface MoneroDkgQrExtras {
	initiatorPartyId: string;
	vaultUid?: string;
	minSigners?: number;
	maxSigners?: number;
	network?: FromtNetwork;
	birthday?: bigint;
}

// As in locketCourier: `encKeyHex` guards the DKG round messages, which
// carry share material, so it takes the hardened path — health-checked,
// and refusing rather than degrading if the generator has failed.
function randomHex(lenBytes: number): string {
	const bytes = generateEntropyBytes(lenBytes);
	return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function createMoneroDkgSession(relayUrl?: string): MoneroDkgSessionParams {
	return {
		sessionId: randomHex(16),
		encKeyHex: randomHex(32),
		relayUrl: relayUrl || DEFAULT_RELAY_URL,
	};
}

function base64urlEncode(bytes: Uint8Array): string {
	let bin = '';
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
	return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlDecode(s: string): Uint8Array {
	let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
	while (b64.length % 4) b64 += '=';
	const bin = atob(b64);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

/**
 * Encode the DKG session params + extras into a compact QR payload of
 * the form `WB32MDKG:<version>:<base64url-json>`. Mirrors the Orchard
 * QR layout (`WB32DKG:`) so we can keep the Cosigner's QR-parsing code
 * symmetrical, but the distinct prefix lets the Cosigner pick the
 * right ceremony runner without sniffing the payload.
 */
export function generateMoneroDkgQR(
	params: MoneroDkgSessionParams,
	extras: MoneroDkgQrExtras,
): string {
	if (!extras?.initiatorPartyId) {
		throw new Error('generateMoneroDkgQR requires extras.initiatorPartyId');
	}
	const payload = {
		s: params.sessionId,
		k: params.encKeyHex,
		r: params.relayUrl,
		i: extras.initiatorPartyId,
		v: extras.vaultUid || undefined,
		t: extras.minSigners || undefined,
		n: extras.maxSigners || undefined,
		net: extras.network || undefined,
		b: extras.birthday !== undefined ? extras.birthday.toString() : undefined,
	};
	const bytes = new TextEncoder().encode(JSON.stringify(payload));
	return `${MDKG_QR_PREFIX}:${MDKG_PROTOCOL_VERSION}:${base64urlEncode(bytes)}`;
}

export function parseMoneroDkgQR(
	qr: string,
): (MoneroDkgSessionParams & MoneroDkgQrExtras) | null {
	if (!qr.startsWith(`${MDKG_QR_PREFIX}:`)) return null;
	const parts = qr.split(':');
	if (parts.length < 3) return null;
	if (parseInt(parts[1], 10) !== MDKG_PROTOCOL_VERSION) return null;
	try {
		const payloadB64 = parts.slice(2).join(':');
		const json = new TextDecoder().decode(base64urlDecode(payloadB64));
		const obj = JSON.parse(json);
		if (!obj?.s || !obj?.k || !obj?.r || !obj?.i) return null;
		const network: FromtNetwork = (
			obj.net === 'testnet' || obj.net === 'stagenet' ? obj.net : 'mainnet'
		);
		return {
			sessionId: String(obj.s),
			encKeyHex: String(obj.k),
			relayUrl: String(obj.r),
			initiatorPartyId: String(obj.i),
			vaultUid: obj.v ? String(obj.v) : undefined,
			minSigners: Number.isFinite(Number(obj.t)) ? Number(obj.t) : undefined,
			maxSigners: Number.isFinite(Number(obj.n)) ? Number(obj.n) : undefined,
			network,
			birthday: obj.b !== undefined ? BigInt(String(obj.b)) : undefined,
		};
	} catch {
		return null;
	}
}

// ── Crypto helpers (AES-GCM envelope) ────────────────────────────────
//
// Identical wire format to orchardDkgCeremony so the same relay can
// shuttle either protocol — peers route by session id + recipient
// role and never need to decrypt.

function hexToBytes(hex: string): Uint8Array {
	const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
	const out = new Uint8Array(clean.length / 2);
	for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
	return out;
}

function toBase64(bytes: Uint8Array): string {
	let s = '';
	for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
	return btoa(s);
}

function fromBase64(b64: string): Uint8Array {
	const bin = atob(b64);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

async function importEncKey(hexKey: string): Promise<CryptoKey> {
	return crypto.subtle.importKey('raw', hexToBytes(hexKey), 'AES-GCM', false, ['encrypt', 'decrypt']);
}

async function deflateBytes(input: Uint8Array): Promise<Uint8Array> {
	const stream = new Blob([input]).stream().pipeThrough(new CompressionStream('deflate-raw'));
	return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function inflateBytes(input: Uint8Array): Promise<Uint8Array> {
	const stream = new Blob([input]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
	return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function sendMsg(
	transport: CosignTransport,
	key: CryptoKey,
	from: string,
	to: string,
	msg: MDkgMsg,
): Promise<void> {
	const payload = new TextEncoder().encode(JSON.stringify(msg));
	const compressed = await deflateBytes(payload);
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const ct = new Uint8Array(
		await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, compressed),
	);
	const combined = new Uint8Array(iv.length + ct.length);
	combined.set(iv);
	combined.set(ct, iv.length);
	await transport.send(from, to, toBase64(combined));
}

async function receiveMsgs(
	transport: CosignTransport,
	key: CryptoKey,
	myRole: string,
): Promise<MDkgMsg[]> {
	const raws = await transport.receive(myRole);
	const out: MDkgMsg[] = [];
	for (const enc of raws) {
		try {
			const combined = fromBase64(enc);
			const iv = combined.slice(0, 12);
			const ciphertext = combined.slice(12);
			const plain = new Uint8Array(
				await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext),
			);
			const json = new TextDecoder().decode(await inflateBytes(plain));
			out.push(JSON.parse(json) as MDkgMsg);
		} catch (err) {
			console.warn(`[mdkg-ceremony] dropped undecodable message for role=${myRole}: ${(err as Error).message}`);
		}
	}
	return out;
}

class CeremonyInbox {
	private buffer: MDkgMsg[] = [];

	async drain(
		transport: CosignTransport,
		key: CryptoKey,
		myRole: string,
	): Promise<void> {
		const fresh = await receiveMsgs(transport, key, myRole);
		if (fresh.length) this.buffer.push(...fresh);
	}

	take(predicate: (m: MDkgMsg) => boolean): MDkgMsg[] {
		const taken: MDkgMsg[] = [];
		const kept: MDkgMsg[] = [];
		for (const m of this.buffer) {
			if (predicate(m)) taken.push(m);
			else kept.push(m);
		}
		this.buffer = kept;
		return taken;
	}

	throwOnError(): void {
		const errs = this.take(
			(m): m is MDkgErrorMsg => m.type === 'mdkg-error',
		) as MDkgErrorMsg[];
		if (errs.length) {
			const first = errs[0];
			throw new Error(`Peer ${first.from || 'unknown'} reported: ${first.message}`);
		}
	}
}

function checkAborted(signal?: AbortSignal): void {
	if (signal?.aborted) throw new Error('Monero DKG ceremony aborted');
}

// ── Result type ──────────────────────────────────────────────────────

export interface MoneroDkgCeremonyResult {
	myPartyId: string;
	frostId: number;
	bundleBytes: Uint8Array;
	network: FromtNetwork;
	birthday: bigint;
	viewKey: Uint8Array;
	minSigners: number;
	maxSigners: number;
	participantPartyIds: string[];
}

export interface MoneroDkgCeremonyCallbacks {
	onProgress?: (phase: string) => void;
	onPartyJoined?: (partyId: string, joinedCount: number, expected: number) => void;
	signal?: AbortSignal;
}

// ── Shared message-pump runner (initiator + joiner both call this) ──

interface RunSharedRoundsArgs {
	transport: CosignTransport;
	key: CryptoKey;
	myPartyId: string;
	partyOrder: string[];
	session: FromtDkgSessionHandle;
	callbacks?: MoneroDkgCeremonyCallbacks;
	deadline: number;
	inbox: CeremonyInbox;
	minSigners: number;
	maxSigners: number;
	network: FromtNetwork;
	birthdayBig: bigint;
}

async function runMoneroDkgRounds(args: RunSharedRoundsArgs): Promise<MoneroDkgCeremonyResult> {
	const {
		transport, key, myPartyId, partyOrder, session, callbacks,
		deadline, inbox, minSigners, maxSigners, network, birthdayBig,
	} = args;

	const myFrostId = partyOrder.indexOf(myPartyId) + 1;
	if (myFrostId <= 0) {
		throw new Error(`Party ${myPartyId} missing from partyOrder — initiator did not include us in the start message`);
	}
	const partyIdByFrostId = new Map<number, string>();
	partyOrder.forEach((pid, i) => partyIdByFrostId.set(i + 1, pid));

	let completed = false;
	callbacks?.onProgress?.('Running Monero FROST DKG…');

	for (let loop = 0; loop < MAX_RELAY_DRIVE_LOOPS && !completed; loop++) {
		checkAborted(callbacks?.signal);
		if (Date.now() > deadline) throw new Error('Monero DKG ceremony timed out');

		// 1) Drain outbound messages and forward them to addressed peers.
		// `takeMsg()` returns `[recipient_u16_le][payload...]` — we strip
		// the recipient prefix, decide who to send to via `msgReceiver`,
		// and ship the bare payload over the relay.
		let outbound = 0;
		let msg: Uint8Array | undefined;
		while ((msg = session.takeMsg())) {
			outbound++;
			const payload = msg.subarray(2);
			const payloadB64 = toBase64(payload);
			// Walk every recipient referenced by this msg. For unicast,
			// only index 0 returns a name; for broadcast, every other
			// party's name appears in turn. We never resend to ourselves.
			for (let idx = 0; idx < partyOrder.length; idx++) {
				const recipient = session.msgReceiver(msg, idx);
				if (!recipient) break;
				if (recipient === myPartyId) continue;
				if (!partyIdByFrostId.has(partyOrder.indexOf(recipient) + 1)) {
					throw new Error(`Monero FROST DKG produced message addressed to unknown party "${recipient}"`);
				}
				await sendMsg(transport, key, myPartyId, recipient, {
					type: 'mdkg-msg',
					fromPartyId: myPartyId,
					fromFrostId: myFrostId,
					payloadB64,
				});
			}
		}

		// 2) Drain the inbox and feed every relay message into the
		// local WASM session. Wrap with `[sender_frost_id_u16_le]`
		// because `feed()` expects that frame (mirrors the in-tab
		// driver in `generateFromtBundlesForFreshVault`).
		await inbox.drain(transport, key, myPartyId);
		inbox.throwOnError();
		const incoming = inbox.take(
			(m): m is MDkgRelayMsg => m.type === 'mdkg-msg',
		) as MDkgRelayMsg[];

		let inboundFed = 0;
		for (const m of incoming) {
			if (m.fromPartyId === myPartyId) continue;
			const payload = fromBase64(m.payloadB64);
			const wrapped = new Uint8Array(2 + payload.length);
			wrapped[0] = m.fromFrostId & 0xff;
			wrapped[1] = (m.fromFrostId >> 8) & 0xff;
			wrapped.set(payload, 2);
			try {
				const done = session.feed(wrapped);
				inboundFed++;
				if (done) completed = true;
			} catch (err) {
				throw new Error(
					`Monero FROST DKG feed() rejected message from ${m.fromPartyId}: ${(err as Error).message}`,
				);
			}
		}

		if (completed) break;

		// No progress this loop → wait a bit before polling again so
		// we don't burn the relay's rate limiter while peers are still
		// computing their next round.
		if (outbound === 0 && inboundFed === 0) {
			await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
		}
	}

	if (!completed) {
		throw new Error('Monero FROST DKG did not complete within the allowed iterations');
	}

	const bundleBytes = session.result();
	const header = parseKeyShareBundleHeader(bundleBytes);

	callbacks?.onProgress?.('DKG complete. Cross-checking view keys…');

	// Broadcast our view-key to every other party so divergence
	// (e.g. someone fed setupBytes for a different vault) is detected
	// before the local bundle is persisted.
	const viewKeyHex = Array.from(header.viewKey)
		.map(b => b.toString(16).padStart(2, '0')).join('');
	const others = partyOrder.filter(p => p !== myPartyId);
	for (const pid of others) {
		await sendMsg(transport, key, myPartyId, pid, {
			type: 'mdkg-done', partyId: myPartyId, viewKeyHex,
		});
	}

	const peerViewKeys: Record<string, string> = {};
	const drainDeadline = Math.min(deadline, Date.now() + 15_000);
	while (
		Object.keys(peerViewKeys).length < others.length &&
		Date.now() < drainDeadline
	) {
		checkAborted(callbacks?.signal);
		await inbox.drain(transport, key, myPartyId);
		inbox.throwOnError();
		const dones = inbox.take(
			(m): m is MDkgDoneMsg => m.type === 'mdkg-done',
		) as MDkgDoneMsg[];
		for (const d of dones) {
			if (others.includes(d.partyId)) peerViewKeys[d.partyId] = d.viewKeyHex;
		}
		if (Object.keys(peerViewKeys).length < others.length) {
			await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
		}
	}
	for (const [pid, vk] of Object.entries(peerViewKeys)) {
		if (vk.toLowerCase() !== viewKeyHex.toLowerCase()) {
			throw new Error(
				`Monero DKG desync: peer ${pid} derived view-key=${vk} but we have ${viewKeyHex}. ` +
				'Refusing to store divergent shares — re-run the ceremony.',
			);
		}
	}

	return {
		myPartyId,
		frostId: myFrostId,
		bundleBytes,
		network,
		birthday: birthdayBig,
		viewKey: header.viewKey,
		minSigners,
		maxSigners,
		participantPartyIds: partyOrder,
	};
}

// ── Initiator orchestrator ──────────────────────────────────────────

export interface RunMoneroDkgInitiatorParams {
	session: MoneroDkgSessionParams;
	myPartyId: string;
	otherPartyIds: string[];
	minSigners: number;
	vaultUid?: string;
	network?: FromtNetwork;
	birthday?: bigint | number;
	callbacks?: MoneroDkgCeremonyCallbacks;
	transport?: CosignTransport;
}

export async function runInitiatorMoneroDkgCeremony(
	params: RunMoneroDkgInitiatorParams,
): Promise<MoneroDkgCeremonyResult> {
	const {
		session: relaySession, myPartyId, otherPartyIds, minSigners,
		vaultUid, callbacks,
	} = params;
	const network: FromtNetwork = params.network || 'mainnet';
	const birthdayBig = typeof params.birthday === 'bigint'
		? params.birthday
		: BigInt(Math.max(0, Math.floor(Number(params.birthday) || 0)));

	const allPartyIds = [myPartyId, ...otherPartyIds];
	if (new Set(allPartyIds).size !== allPartyIds.length) {
		throw new Error('Duplicate partyId in Monero DKG participant list');
	}
	const maxSigners = allPartyIds.length;
	if (minSigners < 2) throw new Error('minSigners must be >= 2');
	if (minSigners > maxSigners) throw new Error('minSigners must be <= maxSigners');

	const wasm = await initFromtFrostWasm();
	const key = await importEncKey(relaySession.encKeyHex);
	const transport = params.transport
		?? createTransport('relay', relaySession.sessionId, relaySession.relayUrl);
	const inbox = new CeremonyInbox();

	const deadline = Date.now() + CEREMONY_TIMEOUT_MS;

	try {
		// Phase A: wait for every joiner to announce.
		callbacks?.onProgress?.(`Waiting for ${otherPartyIds.length} device(s) to join Monero DKG…`);
		const joined = new Set<string>();
		while (joined.size < otherPartyIds.length) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) {
				throw new Error('Monero DKG ceremony timed out waiting for joiners');
			}
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const joins = inbox.take(
				(m): m is MDkgJoinMsg => m.type === 'mdkg-join',
			) as MDkgJoinMsg[];
			for (const m of joins) {
				if (!otherPartyIds.includes(m.partyId)) {
					console.warn(`[mdkg-ceremony] ignoring join from unknown partyId=${m.partyId}`);
					continue;
				}
				if (!joined.has(m.partyId)) {
					joined.add(m.partyId);
					callbacks?.onPartyJoined?.(m.partyId, joined.size, otherPartyIds.length);
				}
			}
			if (joined.size < otherPartyIds.length) {
				await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
			}
		}

		// Phase B: assign frostIds and broadcast the canonical setup.
		// Sorting the partyIds lexically lets every joiner reproduce
		// the same frostId assignment without trusting the initiator.
		const partyOrder = [...allPartyIds].sort();
		const parties: FromtPartyEntry[] = partyOrder.map((name, i) => ({
			frostId: i + 1, name,
		}));
		const partiesData = encodeFromtPartiesData(parties);
		const netByte = networkByte(network);
		const setupBytes = wasm.fromtDkgSetupMsgNew(
			maxSigners,
			minSigners,
			partiesData,
			netByte,
			birthdayBig,
		);

		const startMsg: MDkgStartMsg = {
			type: 'mdkg-start',
			setupB64: toBase64(setupBytes),
			partyOrder,
			minSigners,
			maxSigners,
			network,
			birthdayStr: birthdayBig.toString(),
			vaultUid,
		};
		for (const pid of otherPartyIds) {
			await sendMsg(transport, key, myPartyId, pid, startMsg);
		}
		callbacks?.onProgress?.('All devices joined. Starting Monero DKG…');

		const sessionHandle = wasm.FromtDkgSession.fromSetup(
			setupBytes, myPartyId, netByte, birthdayBig,
		);
		try {
			return await runMoneroDkgRounds({
				transport, key, myPartyId, partyOrder,
				session: sessionHandle, callbacks, deadline, inbox,
				minSigners, maxSigners, network, birthdayBig,
			});
		} finally {
			try { sessionHandle.free(); } catch { /* */ }
		}
	} finally {
		try { transport.close?.(); } catch { /* ignore */ }
	}
}

// ── Joiner orchestrator ─────────────────────────────────────────────

export interface RunMoneroDkgJoinerParams {
	session: MoneroDkgSessionParams;
	myPartyId: string;
	initiatorPartyId: string;
	vaultUid?: string;
	callbacks?: MoneroDkgCeremonyCallbacks;
	transport?: CosignTransport;
}

export async function runJoinerMoneroDkgCeremony(
	params: RunMoneroDkgJoinerParams,
): Promise<MoneroDkgCeremonyResult> {
	const { session: relaySession, myPartyId, initiatorPartyId, vaultUid, callbacks } = params;

	const wasm = await initFromtFrostWasm();
	const key = await importEncKey(relaySession.encKeyHex);
	const transport = params.transport
		?? createTransport('relay', relaySession.sessionId, relaySession.relayUrl);
	const inbox = new CeremonyInbox();

	const deadline = Date.now() + CEREMONY_TIMEOUT_MS;

	try {
		callbacks?.onProgress?.('Announcing join to initiator…');
		await sendMsg(transport, key, myPartyId, initiatorPartyId, {
			type: 'mdkg-join', partyId: myPartyId, vaultUid,
		});

		callbacks?.onProgress?.('Waiting for Monero DKG start message…');
		let start: MDkgStartMsg | null = null;
		while (!start) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) throw new Error('Monero DKG ceremony timed out waiting for start');
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const starts = inbox.take(
				(m): m is MDkgStartMsg => m.type === 'mdkg-start',
			) as MDkgStartMsg[];
			if (starts.length) { start = starts[0]; break; }
			await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
		}

		const partyOrder = start.partyOrder;
		if (!partyOrder.includes(myPartyId)) {
			throw new Error(`Initiator did not include party ${myPartyId} in partyOrder`);
		}
		const birthdayBig = BigInt(start.birthdayStr);
		const netByte = networkByte(start.network);
		const setupBytes = fromBase64(start.setupB64);

		const sessionHandle = wasm.FromtDkgSession.fromSetup(
			setupBytes, myPartyId, netByte, birthdayBig,
		);
		try {
			return await runMoneroDkgRounds({
				transport, key, myPartyId, partyOrder,
				session: sessionHandle, callbacks, deadline, inbox,
				minSigners: start.minSigners, maxSigners: start.maxSigners,
				network: start.network, birthdayBig,
			});
		} finally {
			try { sessionHandle.free(); } catch { /* */ }
		}
	} finally {
		try { transport.close?.(); } catch { /* */ }
	}
}

// ── Error broadcast helper (best-effort) ─────────────────────────────

export async function broadcastMoneroDkgError(
	transport: CosignTransport,
	encKeyHex: string,
	fromPartyId: string,
	toPartyIds: string[],
	message: string,
): Promise<void> {
	try {
		const key = await importEncKey(encKeyHex);
		await Promise.all(toPartyIds.map(pid =>
			sendMsg(transport, key, fromPartyId, pid, {
				type: 'mdkg-error', from: fromPartyId, message,
			}),
		));
	} catch (err) {
		console.warn('[mdkg-ceremony] failed to broadcast error:', (err as Error).message);
	}
}
